import React, { useEffect, useState } from "react";
import { Popup } from "devextreme-react/popup";
import { RadioGroup, SelectBox } from "devextreme-react";
import { MESSAGES } from "../../../../../common/Messages";
import {
    LotCodeDefaultMethodOptions,
    PickSequence,
    ShipQuantityBasis,
    ShipQuantityBasisOptions,
    SortDirectionOptions,
} from "../../../../../enum/TransactionDataEntryEnum";
import { OperationDialogFooter, OperationFieldset } from "./OperationDialogParts";

/**
 * Legacy uOMD0015 "Shipment" dialog. The two group boxes are the Delphi form's: a Ship Quantity Basis
 * radio pair and the Lot Selection pick method / pick sequence combos. Presentational only — the caller
 * runs the operations, the same way SortDetailRecordsPopup (OMD0030) is wired.
 *
 * `showLotSelection` is Delphi's bLotsParam (uOMM0000.pas:4514-4519): with it false the Lot Selection
 * group box is hidden and the form shrinks, because there is nothing to allocate and the dialog is only
 * asking how the shipped quantity should be set.
 *
 * `defaultPickMethod` / `defaultPickSequence` come from the order's transaction profile
 * (OMTRANSACTIONDP.LOTCODEDEFAULTMETHOD / LOTPICKSEQUENCE_SHIPMENTS). Delphi re-created the form on every
 * call, so the fields go back to those defaults each time the dialog opens.
 */
function AutoSelectLotsPopup({
    visible,
    onClose,
    onApply,
    showLotSelection,
    defaultPickMethod,
    defaultPickSequence,
}) {
    const [shipQuantityBasis, setShipQuantityBasis] = useState(ShipQuantityBasis.Automatic);
    const [pickMethod, setPickMethod] = useState(null);
    const [pickSequence, setPickSequence] = useState(PickSequence.Ascending);

    useEffect(() => {
        if (!visible) return;
        // Delphi: rdAuto is the checked radio on the DFM, and the Pick Sequence combo lands on
        // Descending only for a profile value of 'D' — anything else, including blank, is Ascending.
        setShipQuantityBasis(ShipQuantityBasis.Automatic);
        setPickMethod(defaultPickMethod ?? null);
        setPickSequence(
            defaultPickSequence === PickSequence.Descending ? PickSequence.Descending : PickSequence.Ascending
        );
    }, [visible, defaultPickMethod, defaultPickSequence]);

    /* onApply fires before onClose so a caller waiting on the dialog can tell OK from Cancel: OK
       settles with the values, and the onClose that follows is a no-op for it. The Lot Selection values
       are only meaningful when that group box was shown. */
    const handleOk = () => {
        onApply({
            ShipQuantityBasis: shipQuantityBasis,
            LotPickMethod: showLotSelection ? pickMethod : null,
            LotPickSequence: showLotSelection ? pickSequence : null,
        });
        onClose();
    };

    return (
        <Popup
            visible={visible}
            onHiding={onClose}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showTitle={true}
            title={MESSAGES.AUTO_SELECT_LOTS_TITLE}
            width={480}
            height="auto"
            showCloseButton={true}
        >
            <div style={{ padding: "4px 8px 8px 8px" }}>
                {showLotSelection && (
                    <div className="text-muted" style={{ paddingBottom: 10 }}>
                        {MESSAGES.AUTO_SELECT_LOTS_HINT}
                    </div>
                )}
                <OperationFieldset legend="Ship Quantity Basis" first>
                    <RadioGroup
                        items={ShipQuantityBasisOptions}
                        value={shipQuantityBasis}
                        valueExpr="id"
                        displayExpr="name"
                        layout="vertical"
                        onValueChanged={(e) => setShipQuantityBasis(e.value)}
                    />
                </OperationFieldset>
                {showLotSelection && (
                    <OperationFieldset legend="Lot Selection">
                        <SelectBox
                            dataSource={LotCodeDefaultMethodOptions}
                            value={pickMethod}
                            valueExpr="id"
                            displayExpr="name"
                            labelMode="static"
                            stylingMode="outlined"
                            label="Pick Method"
                            onValueChanged={(e) => setPickMethod(e.value)}
                        />
                        <SelectBox
                            dataSource={SortDirectionOptions}
                            value={pickSequence}
                            valueExpr="id"
                            displayExpr="name"
                            labelMode="static"
                            stylingMode="outlined"
                            label="Pick Sequence"
                            className="mt-2"
                            onValueChanged={(e) => setPickSequence(e.value)}
                        />
                    </OperationFieldset>
                )}
                <OperationDialogFooter onOk={handleOk} onCancel={onClose} />
            </div>
        </Popup>
    );
}

export default AutoSelectLotsPopup;
